import { useState } from "react";
import { FiLock, FiCheck, FiAlertCircle } from "react-icons/fi";
import { useAuth } from "../../../auth/AuthProvider";
import { changePassword } from "../../../api/auth";

export default function ChangePasswordCard() {
    const { token } = useAuth();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    const validate = (): string | null => {
        if (!currentPassword) return "Current password is required";
        if (newPassword.length < 8) return "New password must be at least 8 characters";
        if (!/[A-Z]/.test(newPassword)) return "New password must contain an uppercase letter";
        if (!/[0-9]/.test(newPassword)) return "New password must contain a number";
        if (newPassword !== confirmPassword) return "Passwords do not match";
        if (currentPassword === newPassword) return "New password must be different from current password";
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        try {
            await changePassword(token!, currentPassword, newPassword);
            setSuccess("Password changed successfully");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err: any) {
            setError(err.message || "Failed to change password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card">
            <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-quantum-500/10 text-quantum-400">
                    <FiLock className="w-5 h-5" />
                </div>
                <div>
                    <h3 className="text-h4 text-text-primary">Change Password</h3>
                    <p className="text-caption text-text-secondary">Update your account password</p>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 rounded-lg bg-health-500/10 border border-health-500/30 flex items-center gap-2">
                    <FiAlertCircle className="text-health-400 shrink-0" />
                    <p className="text-body-sm text-health-400">{error}</p>
                </div>
            )}
            {success && (
                <div className="mb-4 p-3 rounded-lg bg-ecosystem-500/10 border border-ecosystem-500/30 flex items-center gap-2">
                    <FiCheck className="text-ecosystem-400 shrink-0" />
                    <p className="text-body-sm text-ecosystem-400">{success}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="input-label">Current Password</label>
                    <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="input"
                        autoComplete="current-password"
                    />
                </div>
                <div>
                    <label className="input-label">New Password</label>
                    <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input"
                        autoComplete="new-password"
                    />
                    <p className="mt-1 text-caption text-text-muted">
                        Min 8 characters, at least one uppercase letter and one number
                    </p>
                </div>
                <div>
                    <label className="input-label">Confirm New Password</label>
                    <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="input"
                        autoComplete="new-password"
                    />
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                >
                    {loading ? "Changing..." : "Change Password"}
                </button>
            </form>
        </div>
    );
}
